import { DownChevron, Lock } from '@/components/icons'
import { SubscribeModal } from '@/components/modal'
import { useProfileViews } from '@/hooks/useProfileViews'
import { useUpgradeCta } from '@/hooks/useUpgradeCta'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** Fraction of the allotment already used, 0..1, clamped so it never overflows. */
function usageProgress(viewsUsed: number, viewsLimit: number): number {
  if (viewsLimit <= 0) return 0
  return Math.min(1, Math.min(viewsUsed, viewsLimit) / viewsLimit)
}

const BannerProgressBar = ({ limitReached, progress }: { limitReached: boolean; progress: number }) => {
  return (
    <div className="h-1.5 w-full rounded-full bg-navy-900 bg-opacity-10">
      <div
        className={`h-1.5 rounded-full ${limitReached ? 'bg-boldOrange' : 'bg-primary-500'}`}
        style={{ width: `${progress * 100}%` }}
      />
    </div>
  )
}

const BannerHeadline = ({ limitReached }: { limitReached: boolean }) => {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-x-2">
      {limitReached && <Lock className="w-4 h-4 text-navy-900" />}
      <span className="text-base font-semibold text-navy-900">
        {limitReached ? t('player.profileUnlock.limitReached') : t('player.profileUnlock.banner.runningLow')}
      </span>
    </div>
  )
}

const BannerSubtitle = ({ limitReached, viewsRemaining }: { limitReached: boolean; viewsRemaining: number }) => {
  const { t } = useTranslation()
  return (
    <span className="text-sm text-navy-900 text-opacity-70">
      {limitReached
        ? t('player.profileUnlock.limitReachedSubtitle')
        : t('player.profileUnlock.banner.freeViewsLeft', { count: viewsRemaining })}
    </span>
  )
}

type ExpandedProps = {
  viewsRemaining: number
  limitReached: boolean
  progress: number
  onCollapse: () => void
  onUpgrade: () => void
}

const BannerExpanded = ({ viewsRemaining, limitReached, progress, onCollapse, onUpgrade }: ExpandedProps) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-y-3">
      <button className="self-end text-navy-900 hover:opacity-60" onClick={onCollapse}>
        <DownChevron className="w-5 h-5" />
      </button>
      <BannerProgressBar limitReached={limitReached} progress={progress} />
      <BannerHeadline limitReached={limitReached} />
      <BannerSubtitle limitReached={limitReached} viewsRemaining={viewsRemaining} />
      <button
        className="w-full rounded-full bg-primary-500 py-2.5 text-sm font-semibold text-white hover:bg-opacity-70"
        onClick={onUpgrade}
      >
        {t('player.profileUnlock.banner.unlockUnlimited')}
      </button>
    </div>
  )
}

const CollapsedBadge = ({ limitReached, viewsRemaining }: { limitReached: boolean; viewsRemaining: number }) => {
  return (
    <div className="w-9 h-9 shrink-0 rounded-full bg-primary-500 bg-opacity-10 flex items-center justify-center">
      {limitReached ? (
        <Lock className="w-4 h-4 text-primary-500" />
      ) : (
        <span className="text-sm font-bold text-primary-500">{viewsRemaining}</span>
      )}
    </div>
  )
}

const CollapsedText = ({ limitReached, viewsRemaining }: { limitReached: boolean; viewsRemaining: number }) => {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-semibold text-navy-900">
        {limitReached
          ? t('player.profileUnlock.limitReached')
          : t('player.profileUnlock.banner.viewsLeft', { count: viewsRemaining })}
      </span>
      <span className="truncate text-xs text-navy-900 text-opacity-60">{t('player.profileUnlock.upgradeLink')}</span>
    </div>
  )
}

type CollapsedProps = {
  viewsRemaining: number
  limitReached: boolean
  onExpand: () => void
  onUpgrade: () => void
}

const BannerCollapsed = ({ viewsRemaining, limitReached, onExpand, onUpgrade }: CollapsedProps) => {
  const { t } = useTranslation()

  const handleUpgrade = (e: MouseEvent) => {
    e.stopPropagation()
    onUpgrade()
  }

  return (
    <div className="flex items-center gap-x-3 cursor-pointer" onClick={onExpand}>
      <CollapsedBadge limitReached={limitReached} viewsRemaining={viewsRemaining} />
      <CollapsedText limitReached={limitReached} viewsRemaining={viewsRemaining} />
      <button
        className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-opacity-70"
        onClick={handleUpgrade}
      >
        {t('player.profileUnlock.upgrade')}
      </button>
    </div>
  )
}

/**
 * Floating banner shown at the bottom of a visited profile while the viewer is
 * profile-view limited (even on already-unlocked profiles). Collapsed it shows
 * the remaining count and an Upgrade pill; expanded it adds a usage progress
 * bar and a full-width upsell CTA. Starts expanded when almost out of views.
 */
export const ProfileLimitBanner = () => {
  const { viewsUsed, viewsLimit, viewsRemaining, viewsLimitReached } = useProfileViews()
  const [expanded, setExpanded] = useState(viewsRemaining <= 1 || viewsLimitReached)
  const { openUpgrade, subscribeOpen, closeSubscribe } = useUpgradeCta('profile_limit_cta_clicked')

  return (
    <>
      <div className="fixed bottom-20 lg:bottom-6 inset-x-4 md:inset-x-auto md:right-8 md:w-96 z-40">
        <div className="rounded-2xl bg-white p-4 shadow-xl">
          {expanded ? (
            <BannerExpanded
              viewsRemaining={viewsRemaining}
              limitReached={viewsLimitReached}
              progress={usageProgress(viewsUsed, viewsLimit)}
              onCollapse={() => setExpanded(false)}
              onUpgrade={openUpgrade}
            />
          ) : (
            <BannerCollapsed
              viewsRemaining={viewsRemaining}
              limitReached={viewsLimitReached}
              onExpand={() => setExpanded(true)}
              onUpgrade={openUpgrade}
            />
          )}
        </div>
      </div>
      <SubscribeModal open={subscribeOpen} onClose={closeSubscribe} />
    </>
  )
}
